import React, { useState, useEffect } from "react";
import { Popover, Position, PopoverInteractionKind } from "@blueprintjs/core";
import RemainingFieldsPopover from "./RemainingFieldsPopover";
import "./RemainingFields.scss";
import { AppToasterV2 } from "@ucl/library";

interface RemainingFieldsProps {
  /** The count of remaining fields/tasks */
  remainingCount: number;
  /** The total count of fields/tasks */
  totalCount: number;
  /** Optional label to display before the count */
  label?: string;
  /** Optional CSS class name for custom styling */
  className?: string;
  /**
   * List of remaining field names to display in the popover.
   * If provided, will be used directly without any async loading.
   */
  remainingFields?: string[];
  /**
   * Promise that resolves to a list of remaining field names.
   * If provided and no remainingFields are given, will show loading state
   * while the promise resolves, then display the results.
   */
  remainingFieldsPromiseFactory?: () => Promise<string[]>;
  /**
   * Whether to enable the popover functionality.
   * Must be true for either remainingFields or remainingFieldsPromise to work.
   */
  showFieldDetails?: boolean;
  /**
   * Label to display in the popover header. If not provided, will use the label prop.
   */
  popoverLabel?: string;
}

/**
 * RemainingFields component displays a count of remaining items with optional popover details.
 *
 * @example
 * // Basic usage without popover
 * <RemainingFields remainingCount={3} totalCount={10} />
 *
 * @example
 * // With static field list
 * <RemainingFields
 *   remainingCount={3}
 *   totalCount={10}
 *   showFieldDetails={true}
 *   remainingFields={["Name", "Email", "Phone"]}
 * />
 *
 * @example
 * // With promise-based field loading
 * <RemainingFields
 *   remainingCount={3}
 *   totalCount={10}
 *   showFieldDetails={true}
 *   remainingFieldsPromise={fetchRemainingFields()}
 * />
 */
export const RemainingFields: React.FC<RemainingFieldsProps> = ({
  remainingCount,
  totalCount,
  label,
  className = "",
  remainingFields,
  remainingFieldsPromiseFactory,
  showFieldDetails = false,
  popoverLabel,
}) => {
  const [fields, setFields] = useState<string[] | undefined>(remainingFields);
  const [isLoading, setIsLoading] = useState(false);

  const isComplete = remainingCount === 0;
  const hasFieldDetails = showFieldDetails && fields;

  useEffect(() => {
    if (remainingFields) {
      setFields(remainingFields);
    }
  }, [remainingFields]);

  return (
    <div className={`remaining-fields-container ${className}`}>
      <Popover
        content={
          <RemainingFieldsPopover
            fields={fields}
            isLoading={isLoading}
            totalCount={totalCount}
            popoverLabel={popoverLabel}
            label={label}
          />
        }
        position={Position.BOTTOM}
        interactionKind={PopoverInteractionKind.HOVER}
        portalClassName="remaining-fields-popover"
        isOpen={(fields || remainingCount > 0) ? undefined : false} // do not allow opening if there are no fields or remaining count (in the case of promise-based loading)
        onOpening={async () => {
          if (remainingCount > 0 && remainingFieldsPromiseFactory) {
            setIsLoading(true);
            try {
              const fieldList = await remainingFieldsPromiseFactory();
              setFields(fieldList);
            } catch (error) {
              AppToasterV2.showFailedActionToaster("Failed to load data", "Error");
            } finally {
              setIsLoading(false);
            }
          }
        }}
      >
        <span
          className={`remaining-fields ${hasFieldDetails ? "remaining-fields--interactive" : ""}`}
        >
          {label && <span className="remaining-fields__label">{label}</span>}
          <span className="remaining-fields__count">
            <span className={isComplete ? "remaining-fields__count-number--complete" : "remaining-fields__count-number"}>
              {remainingCount}
            </span>
            <span className="remaining-fields__count-divider">/</span>
            <span className="remaining-fields__count-total">{totalCount}</span>
          </span>
        </span>
      </Popover>
    </div>
  );
};

export default RemainingFields;
